import { useApdmCampaignWizard } from '@euler/functions/apdm/useApdmCampaignWizard';
import { ApdmAudienceRule, ApdmCampaignCondition } from '@euler/model/apdm';
import { useEffect, useState } from 'react';
import { Text, View } from 'react-native';
import { useTheme } from 'styled-components';
import {
  ReviewAction,
  ReviewChoice,
  ReviewField,
} from '../../imports/ReviewControls';
import {
  WizardFormProps,
  WizardNumberField,
  WizardToggle,
} from './WizardControls';
const fields: Array<{ value: ApdmCampaignCondition['field']; label: string }> =
  [
    { value: 'customerName', label: '客户姓名' },
    { value: 'customerPhone', label: '客户电话' },
    { value: 'vehicleBrandModel', label: '品牌车型' },
    { value: 'plateNo', label: '车牌' },
    { value: 'vin', label: 'VIN（可选条件）' },
    { value: 'visitCount', label: '到店次数' },
    { value: 'lastServiceMileage', label: '最近服务里程' },
    { value: 'lastVisitAt', label: '最近到店时间' },
    { value: 'lastServiceAt', label: '最近服务时间' },
    { value: 'nextDueAt', label: '下次保养时间' },
  ];
function newCondition(
  field: ApdmCampaignCondition['field'],
): ApdmCampaignCondition {
  switch (field) {
    case 'visitCount':
    case 'lastServiceMileage':
      return { field, operator: 'gte', value: 0 };
    case 'lastVisitAt':
    case 'lastServiceAt':
    case 'nextDueAt':
      return { field, operator: 'before', value: '' };
    default:
      return { field, operator: 'equals', value: '' };
  }
}
/** The plates in what was typed: one per line, or split by spaces or commas. */
function parsePlates(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/[\s,，、]+/)
        .map(plate => plate.trim().toUpperCase())
        .filter(Boolean),
    ),
  ];
}

/**
 * The plates a campaign names. The text is kept as typed, so a line break
 * can be typed, and follows the draft when it changes elsewhere (the
 * server writes plates as the store does).
 */
function PlatesField({
  plates,
  disabled,
  onChange,
}: {
  plates: string[];
  disabled?: boolean;
  onChange: (plates: string[]) => void;
}) {
  const [text, setText] = useState(plates.join('\n'));
  useEffect(() => {
    setText(current =>
      parsePlates(current).join() === plates.join()
        ? current
        : plates.join('\n'),
    );
  }, [plates]);
  return (
    <ReviewField
      label="客户车牌（每行一个）"
      value={text}
      multiline
      placeholder={'川A12345\n川A67890'}
      disabled={disabled}
      onChange={value => {
        setText(value);
        onChange(parsePlates(value));
      }}
    />
  );
}

export function WizardAudience({
  draft,
  disabled,
  onChange,
  wizard,
  onImport,
}: WizardFormProps & {
  wizard: ReturnType<typeof useApdmCampaignWizard>;
  onImport: () => void;
}) {
  const theme = useTheme();
  const rule = draft.audience.rule;
  const sources = draft.audience.sources;
  const changeRule = (next: ApdmAudienceRule) =>
    onChange({ ...draft, audience: { ...draft.audience, rule: next } });
  const imported = sources.find(source => source.kind === 'imports');
  const selected = imported?.kind === 'imports' ? imported.importIds : [];
  const toggleImport = (id: string, enabled: boolean) => {
    const ids = enabled
      ? [...new Set([...selected, id])]
      : selected.filter(value => value !== id);
    onChange({
      ...draft,
      audience: {
        ...draft.audience,
        sources: [
          ...sources.filter(source => source.kind !== 'imports'),
          ...(ids.length ? [{ kind: 'imports' as const, importIds: ids }] : []),
        ],
      },
    });
  };
  const condition = (index: number, next: ApdmCampaignCondition) => {
    if (rule.kind === 'custom')
      changeRule({
        ...rule,
        conditions: rule.conditions.map((entry, ordinal) =>
          ordinal === index ? next : entry,
        ),
      });
  };
  return (
    <>
      <ReviewChoice
        label="人群规则"
        value={rule.kind}
        disabled={disabled}
        options={[
          { value: 'win_back', label: '长期未到店' },
          { value: 'maintenance_due', label: '保养将到期／已逾期' },
          { value: 'all_reachable', label: '全部可触达客户' },
          { value: 'custom', label: '自定义条件' },
          { value: 'plates', label: '指定车牌' },
        ]}
        onChange={kind =>
          changeRule(
            kind === 'win_back'
              ? { kind, idleMonths: 10 }
              : kind === 'maintenance_due'
              ? { kind, withinDays: 14, overdueUpToDays: 60 }
              : kind === 'custom'
              ? { kind, conditions: [] }
              : kind === 'plates'
              ? { kind, plates: [] }
              : { kind: 'all_reachable' },
          )
        }
      />
      {rule.kind === 'win_back' && (
        <WizardNumberField
          label="未到店月数"
          value={rule.idleMonths}
          disabled={disabled}
          onChange={idleMonths => changeRule({ ...rule, idleMonths })}
        />
      )}
      {rule.kind === 'maintenance_due' && (
        <>
          <WizardNumberField
            label="未来多少天内到期"
            value={rule.withinDays}
            disabled={disabled}
            onChange={withinDays => changeRule({ ...rule, withinDays })}
          />
          <WizardNumberField
            label="包含已逾期天数"
            value={rule.overdueUpToDays}
            disabled={disabled}
            onChange={overdueUpToDays =>
              changeRule({ ...rule, overdueUpToDays })
            }
          />
        </>
      )}
      {rule.kind === 'plates' && (
        <PlatesField
          plates={rule.plates}
          disabled={disabled}
          onChange={plates => changeRule({ ...rule, plates })}
        />
      )}
      {rule.kind === 'custom' && (
        <>
          {rule.conditions.map((entry, index) => (
            <View key={index} style={{ marginBottom: 12 }}>
              <ReviewChoice
                label={`条件 ${index + 1}`}
                value={entry.field}
                options={fields}
                disabled={disabled}
                onChange={field => condition(index, newCondition(field))}
              />
              {'value' in entry && typeof entry.value === 'number' ? (
                <>
                  <ReviewChoice
                    label="比较方式"
                    value={entry.operator as 'gte' | 'lte'}
                    options={[
                      { value: 'gte', label: '至少' },
                      { value: 'lte', label: '至多' },
                    ]}
                    disabled={disabled}
                    onChange={operator => {
                      if (
                        entry.field === 'visitCount' ||
                        entry.field === 'lastServiceMileage'
                      )
                        condition(index, { ...entry, operator });
                    }}
                  />
                  <WizardNumberField
                    label="条件值"
                    value={entry.value}
                    disabled={disabled}
                    onChange={value => {
                      if (
                        entry.field === 'visitCount' ||
                        entry.field === 'lastServiceMileage'
                      )
                        condition(index, { ...entry, value });
                    }}
                  />
                </>
              ) : (
                <>
                  <ReviewChoice
                    label="比较方式"
                    value={entry.operator}
                    disabled={disabled}
                    options={
                      entry.field === 'lastVisitAt' ||
                      entry.field === 'lastServiceAt' ||
                      entry.field === 'nextDueAt'
                        ? [
                            { value: 'before', label: '早于' },
                            { value: 'after', label: '晚于' },
                          ]
                        : [
                            { value: 'equals', label: '等于' },
                            { value: 'contains', label: '包含' },
                          ]
                    }
                    onChange={operator => {
                      if (
                        (entry.field === 'lastVisitAt' ||
                          entry.field === 'lastServiceAt' ||
                          entry.field === 'nextDueAt') &&
                        (operator === 'before' || operator === 'after')
                      )
                        condition(index, { ...entry, operator });
                      else if (
                        (entry.field === 'customerName' ||
                          entry.field === 'customerPhone' ||
                          entry.field === 'vehicleBrandModel' ||
                          entry.field === 'plateNo' ||
                          entry.field === 'vin') &&
                        (operator === 'equals' || operator === 'contains')
                      )
                        condition(index, {
                          field: entry.field,
                          value: String(entry.value),
                          operator,
                        });
                    }}
                  />
                  <ReviewField
                    label="条件值（日期使用 ISO UTC）"
                    value={String(entry.value)}
                    disabled={disabled}
                    onChange={value => {
                      if (
                        entry.operator === 'equals' ||
                        entry.operator === 'contains' ||
                        entry.operator === 'before' ||
                        entry.operator === 'after'
                      )
                        condition(index, { ...entry, value });
                    }}
                  />
                </>
              )}
              <ReviewAction
                title="删除此条件"
                disabled={disabled}
                onPress={() =>
                  changeRule({
                    ...rule,
                    conditions: rule.conditions.filter(
                      (_, ordinal) => ordinal !== index,
                    ),
                  })
                }
              />
            </View>
          ))}
          <ReviewAction
            title="添加条件"
            disabled={disabled}
            onPress={() =>
              changeRule({
                ...rule,
                conditions: [...rule.conditions, newCondition('visitCount')],
              })
            }
          />
        </>
      )}
      <WizardToggle
        label="平台客户数据"
        value={sources.some(source => source.kind === 'platform')}
        disabled={disabled || wizard.state.options?.platformAvailable !== true}
        onChange={enabled =>
          onChange({
            ...draft,
            audience: {
              ...draft.audience,
              sources: [
                ...sources.filter(source => source.kind !== 'platform'),
                ...(enabled ? [{ kind: 'platform' as const }] : []),
              ],
            },
          })
        }
      />
      {wizard.state.options?.platformAvailable !== true && (
        <Text style={{ color: theme.label.color }}>
          本门店当前没有可用平台数据。
        </Text>
      )}
      <Text style={{ color: theme.label.color, marginBottom: 12 }}>
        仅显示实际已发布的服务历史导入；VIN 与里程不是导入必填项。
      </Text>
      {wizard.state.imports.map(item => (
        <WizardToggle
          key={item.id}
          label={`${item.fileName} · ${item.visits} 条来店记录`}
          value={selected.includes(item.id)}
          disabled={disabled}
          onChange={enabled => toggleImport(item.id, enabled)}
        />
      ))}
      <ReviewAction
        title="导入服务历史（确认后返回刷新）"
        disabled={disabled}
        onPress={onImport}
      />
      <ReviewAction
        title="刷新已发布导入"
        disabled={disabled}
        onPress={() => {
          void wizard.actions.refreshCatalog();
        }}
      />
      {wizard.state.nextImportId && (
        <ReviewAction
          title="加载更多已发布导入"
          disabled={disabled}
          onPress={() => {
            void wizard.actions.refreshCatalog(false, true);
          }}
        />
      )}
    </>
  );
}
